import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import LearningHardRedirect from '../../../components/common/LearningHardRedirect';

/**
 * InternshipRoute — protects /internship/* routes.
 *
 * Rules:
 *  - Not authenticated            → /login
 *  - module !== 'internship'      → external learning app
 *  - module_status = 'pending_payment' → /internship/payment
 *  - module_status = 'active' or 'completed' → render children ✅
 */
const InternshipRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner size="md" label="Loading…" fullPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.module !== 'internship') {
    return <LearningHardRedirect />;
  }

  if (user?.module_status === 'pending_payment' && user?.partner !== 'twingp') {
    return <Navigate to="/internship/payment" replace />;
  }

  return children;
};

/**
 * InternshipBrowseRoute — allows pending_payment users to reach project
 * listing and project detail pages. They see a locked UI, not a redirect.
 *
 * Blocks:
 *  - Not authenticated       → /login
 *  - module !== 'internship' → external learning app
 *
 * Allows through (renders children):
 *  - module_status = 'pending_payment'  (shows locked/subscribe UI)
 *  - module_status = 'active'
 *  - module_status = 'completed'
 */
export const InternshipBrowseRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner size="md" label="Loading…" fullPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.module !== 'internship') {
    return <LearningHardRedirect />;
  }

  return children;
};

export default InternshipRoute;
